import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl } from "../lib/api";
import { toast } from "sonner";
import Header from "../components/Header";
import { ClienteLogin } from "../components/ClienteLogin";
import { LogOut, XCircle, Bell, Truck, Home, Package, PlayCircle, ClipboardList, CalendarDays, User, Repeat, Star } from "lucide-react";
import { inscreverPush } from "../lib/push";

const fmtD = (s) => (s ? s.split("-").reverse().join("/") : "-");

export default function MinhaConta() {
  const [token, setToken] = useState(localStorage.getItem("client_token") || "");
  const [googleUser, setGoogleUser] = useState(null);
  const [nome, setNome] = useState("");
  const [dados, setDados] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [orcamentos, setOrcamentos] = useState([]);
  const [notifBanner, setNotifBanner] = useState(false);
  const [rascunho, setRascunho] = useState(null);
  const [verOrcamento, setVerOrcamento] = useState(null);
  const [view, setView] = useState("home");
  const [areas, setAreas] = useState(null);
  const [pubSettings, setPubSettings] = useState(null);

  const abrirOrcamento = async (quoteId) => {
    try {
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const r = await api.get(`/cliente/orcamentos/${quoteId}`, cfg);
      setVerOrcamento(r.data.quote || r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível abrir o orçamento");
    }
  };

  const ativarNotificacoes = async () => {
    const ok = await inscreverPush("/push/subscribe/cliente");
    if (ok) { toast.success("Notificações ativadas! Você receberá avisos do seu frete mesmo com o app fechado."); setNotifBanner(false); }
    else toast.error("Não foi possível ativar. Verifique a permissão de notificações do navegador.");
  };

  const load = (t, g) => {
    const req = g
      ? api.get("/cliente/google/servicos")
      : api.get("/cliente/servicos", { headers: { Authorization: `Bearer ${t}` } });
    req.then((r) => { setOrcamentos(r.data.orcamentos || []); setLoaded(true); })
      .catch(() => { localStorage.removeItem("client_token"); setToken(""); setGoogleUser(null); setLoaded(false); });
  };
  useEffect(() => {
    if (token) {
      load(token, null);
      api.get("/cliente/me", { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => { if (r.data) { setDados((d) => ({ ...r.data, ...d })); if (r.data.nome && !nome) setNome(r.data.nome); } })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => {
    if (!localStorage.getItem("client_token")) {
      api.get("/cliente/google/me").then((r) => { setGoogleUser(r.data); setNome(r.data.nome); }).catch(() => {});
    }
  }, []);
  useEffect(() => { if (googleUser) { setDados((d) => ({ ...d, email: googleUser.email })); load(null, googleUser); } }, [googleUser]);
  useEffect(() => {
    if (!token && !googleUser) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "granted") inscreverPush("/push/subscribe/cliente");
    else if (Notification.permission === "default") setNotifBanner(true);
  }, [token, googleUser]);
  useEffect(() => {
    if (!token && !googleUser) return;
    const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    api.get("/cliente/rascunho", cfg).then((r) => setRascunho(r.data?.rascunho || null)).catch(() => {});
    api.get("/cliente/areas", cfg).then((r) => setAreas(r.data?.areas || {})).catch(() => {});
    api.get("/public/settings").then((r) => setPubSettings(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, googleUser]);

  const sair = () => {
    if (googleUser) { api.post("/auth/logout").catch(() => {}); setGoogleUser(null); }
    localStorage.removeItem("client_token");
    setToken("");
    setNome("");
    setDados({});
    setLoaded(false);
  };

  const OrcamentoCard = ({ o }) => (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`orcamento-card-${o.quote_id}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="font-heading font-bold text-navy text-sm">{(o.tipo === "frete" ? "FRETE" : o.tipo === "entrega" ? "ENTREGA" : "MUDANÇA")} · {(o.quote_id || "").replace("q_", "#")}</span>
        <span data-testid={`orcamento-selo-${o.quote_id}`} className={`text-[9px] font-extrabold px-2 py-1 rounded-full text-center ${o.situacao === "aguardando_pagamento" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
          {o.situacao === "aguardando_pagamento" ? "AGUARDANDO PAGAMENTO" : "SINAL EM ANÁLISE"}
        </span>
      </div>
      <p className="text-xs text-slate-600">{fmtD(o.data)} às {o.horario}</p>
      <p className="text-xs text-slate-600">{o.origem} → {o.destino}</p>
      <p className="text-xs text-slate-600 mt-1">Valor atual: <strong>{brl(o.total)}</strong></p>
      <p className="text-[10px] text-slate-400 mt-1">
        Criado em {o.created_at ? new Date(o.created_at).toLocaleDateString("pt-BR") : "—"}
        {o.editado_em ? ` · última alteração ${new Date(o.editado_em).toLocaleString("pt-BR")}` : ""}
      </p>
      <div className="mt-2 flex gap-2">
        <button onClick={() => abrirOrcamento(o.quote_id)} data-testid={`visualizar-orcamento-${o.quote_id}`}
          className="flex-1 py-2.5 rounded-xl bg-slate-100 text-navy text-[11px] font-extrabold active:scale-95 transition-transform">
          VISUALIZAR
        </button>
        {o.situacao === "aguardando_pagamento" && (
          <Link to={`/orcamento?continuar=${o.quote_id}`} data-testid={`continuar-pagamento-${o.quote_id}`}
            className="flex-1 py-2.5 rounded-xl bg-[#F5B301] text-navy text-[11px] font-extrabold text-center active:scale-95 transition-transform">
            CONTINUAR PAGAMENTO
          </Link>
        )}
        {o.editavel ? (
          <Link to={`/orcamento?editar=${o.quote_id}`} data-testid={`alterar-orcamento-${o.quote_id}`}
            className="flex-1 py-2.5 rounded-xl border-2 border-navy text-navy text-[11px] font-extrabold text-center active:scale-95 transition-transform">
            ALTERAR
          </Link>
        ) : (
          <span data-testid={`orcamento-bloqueado-${o.quote_id}`}
            className="flex-1 py-2.5 rounded-xl bg-slate-100 border border-slate-200 text-slate-400 text-[9px] font-extrabold text-center flex items-center justify-center">
            CONFIRMADO PELA EMPRESA
          </span>
        )}
      </div>
      {o.situacao !== "aguardando_pagamento" && (
        <p className="text-[10px] text-slate-400 mt-2">Seu sinal foi informado e está aguardando a confirmação da empresa.</p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0b1220] flex justify-center">
      <div className="w-full max-w-md mx-auto min-h-screen bg-slate-50 shadow-2xl pb-10" data-testid="minha-conta-page">
        <Header right={(token || googleUser) && <button data-testid="conta-logout-btn" onClick={sair} className="p-2 text-slate-400 hover:text-red-500"><LogOut className="w-5 h-5" /></button>} />
        {verOrcamento && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" data-testid="ver-orcamento-modal" onClick={() => setVerOrcamento(null)}>
            <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold text-navy">Orçamento {(verOrcamento.quote_id || "").replace("q_", "#")}</h3>
                <button data-testid="ver-orcamento-fechar" onClick={() => setVerOrcamento(null)} className="text-slate-400 hover:text-slate-600"><XCircle className="w-5 h-5" /></button>
              </div>
              <div className="space-y-1.5 text-xs text-slate-600">
                <p><strong>Tipo:</strong> {verOrcamento.tipo === "frete" ? "Frete" : verOrcamento.tipo === "entrega" ? "Entrega" : "Mudança"} · <strong>Status:</strong> {verOrcamento.status}</p>
                <p><strong>Criado em:</strong> {verOrcamento.created_at ? new Date(verOrcamento.created_at).toLocaleString("pt-BR") : "—"}</p>
                {verOrcamento.editado_em && <p><strong>Última alteração:</strong> {new Date(verOrcamento.editado_em).toLocaleString("pt-BR")}</p>}
                <p><strong>Data pretendida:</strong> {fmtD(verOrcamento.agendamento?.data)} às {verOrcamento.agendamento?.horario}</p>
                <p><strong>Origem:</strong> {verOrcamento.origin?.logradouro}, {verOrcamento.origin?.numero} — {verOrcamento.origin?.bairro}, {verOrcamento.origin?.cidade}/{verOrcamento.origin?.uf}</p>
                <p><strong>Destino:</strong> {verOrcamento.destination?.logradouro}, {verOrcamento.destination?.numero} — {verOrcamento.destination?.bairro}, {verOrcamento.destination?.cidade}/{verOrcamento.destination?.uf}</p>
                <p><strong>Itens:</strong> {Object.entries(verOrcamento.items || {}).filter(([, v]) => v).map(([k, v]) => `${k} x${v}`).join(", ") || "—"}{verOrcamento.outros_itens ? ` · outros: ${verOrcamento.outros_itens}` : ""}</p>
                {(verOrcamento.edits || []).length > 0 && (
                  <div data-testid="ver-orcamento-historico">
                    <p className="font-bold text-navy pt-1">Histórico de alterações:</p>
                    {verOrcamento.edits.map((e, i) => (
                      <p key={i} className="text-[10px] text-slate-400">
                        {new Date(e.em).toLocaleString("pt-BR")} — {(e.campos || []).map((c) => c.campo).join(", ") || "edição"}
                      </p>
                    ))}
                  </div>
                )}
                <p className="text-sm pt-1"><strong>Valor atual: {brl(verOrcamento.final_value)}</strong></p>
              </div>
            </div>
          </div>
        )}

        {!token && !googleUser ? (
          <ClienteLogin
            titulo="Minha Conta"
            subtitulo="Entre com sua conta Google ou com o telefone e o CPF informados no orçamento para acessar sua conta."
            redirectPath="/minha-conta"
            prefix="conta"
            onToken={(t, n, d) => { setNome(n); setDados(d || {}); setToken(t); }}
          />
        ) : !loaded ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#F5B301] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="px-4 pt-5 space-y-6">
            <div className="flex items-center justify-between">
              <span className="inline-block text-[10px] font-extrabold bg-navy text-[#F5B301] px-3 py-1 rounded-full uppercase tracking-wide" data-testid="perfil-conta-label">
                👤 Minha Conta
              </span>
              {view !== "home" && (
                <button data-testid="hub-voltar-inicio" onClick={() => setView("home")} className="text-[10px] font-extrabold text-navy underline underline-offset-2 uppercase tracking-wide">
                  ← Início
                </button>
              )}
            </div>
            {nome && <p className="text-sm text-slate-500">Olá, <strong className="text-navy">{nome}</strong>.</p>}
            {view === "home" && notifBanner && (
              <div className="bg-white rounded-2xl border border-[#F5B301]/60 shadow-sm p-4 flex items-center gap-3" data-testid="cliente-notif-banner">
                <Bell className="w-6 h-6 text-[#F5B301] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-navy">Ative as notificações</p>
                  <p className="text-[10px] text-slate-500">Receba avisos do seu frete mesmo com o app fechado.</p>
                </div>
                <button data-testid="cliente-notif-ativar-btn" onClick={ativarNotificacoes}
                  className="py-2 px-3 rounded-xl bg-navy text-[#F5B301] text-[10px] font-extrabold active:scale-95 transition-transform shrink-0">
                  ATIVAR
                </button>
                <button data-testid="cliente-notif-fechar-btn" onClick={() => setNotifBanner(false)} className="text-slate-300 hover:text-slate-500 text-sm shrink-0 px-1">✕</button>
              </div>
            )}
            {view === "home" && (
              <div className="space-y-3" data-testid="hub-home">
                {rascunho ? (
                  <Link to="/orcamento" data-testid="hub-continuar-orcamento"
                    className="w-full p-4 rounded-2xl bg-gold-light border-2 border-[#F5B301] flex items-center gap-3 active:scale-[0.98] transition-transform">
                    <PlayCircle className="w-6 h-6 text-navy shrink-0" />
                    <div className="flex-1 min-w-0 text-left">
                      <p className="font-heading font-bold text-navy text-sm">CONTINUAR ORÇAMENTO</p>
                      <p className="text-[11px] text-slate-500">Retome de onde parou — salvo em {new Date(rascunho.atualizado_em).toLocaleString("pt-BR")}</p>
                    </div>
                  </Link>
                ) : orcamentos.length > 0 ? (
                  <button data-testid="hub-continuar-orcamento" onClick={() => setView("orcamentos")}
                    className="w-full p-4 rounded-2xl bg-gold-light border-2 border-[#F5B301] flex items-center gap-3 active:scale-[0.98] transition-transform text-left">
                    <PlayCircle className="w-6 h-6 text-navy shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-bold text-navy text-sm">CONTINUAR ORÇAMENTO</p>
                      <p className="text-[11px] text-slate-500">{orcamentos.length === 1 ? "1 orçamento em andamento — toque para escolher" : `${orcamentos.length} orçamentos em andamento — toque para escolher`}</p>
                    </div>
                  </button>
                ) : (
                  <div className="w-full p-4 rounded-2xl bg-white border border-slate-100 flex items-center gap-3" data-testid="hub-sem-orcamento">
                    <PlayCircle className="w-6 h-6 text-slate-300 shrink-0" />
                    <p className="text-[11px] text-slate-400">Nenhum orçamento em andamento — comece uma nova solicitação abaixo.</p>
                  </div>
                )}

                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="hub-nova-solicitacao">
                  <p className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2">Nova solicitação</p>
                  <div className="space-y-2">
                    <Link to="/orcamento?tipo=frete" data-testid="hub-nova-frete"
                      className="w-full p-3.5 rounded-xl border-2 border-slate-200 flex items-center gap-3 active:scale-[0.98] transition-all">
                      <Truck className="w-5 h-5 text-navy shrink-0" />
                      <div className="text-left">
                        <p className="font-heading font-bold text-navy text-xs">FRETE</p>
                        <p className="text-[10px] text-slate-500">Um item ou poucos itens</p>
                      </div>
                    </Link>
                    <Link to="/orcamento?tipo=mudanca" data-testid="hub-nova-mudanca"
                      className="w-full p-3.5 rounded-xl border-2 border-slate-200 flex items-center gap-3 active:scale-[0.98] transition-all">
                      <Home className="w-5 h-5 text-navy shrink-0" />
                      <div className="text-left">
                        <p className="font-heading font-bold text-navy text-xs">MUDANÇA</p>
                        <p className="text-[10px] text-slate-500">Vários móveis, caixas e objetos</p>
                      </div>
                    </Link>
                    {pubSettings?.entrega_ativa && (
                      <Link to="/orcamento?tipo=entrega" data-testid="hub-nova-entrega"
                        className="w-full p-3.5 rounded-xl border-2 border-slate-200 flex items-center gap-3 active:scale-[0.98] transition-all">
                        <Package className="w-5 h-5 text-navy shrink-0" />
                        <div className="text-left">
                          <p className="font-heading font-bold text-navy text-xs">ENTREGA</p>
                          <p className="text-[10px] text-slate-500">Entrega rápida de pacotes</p>
                        </div>
                      </Link>
                    )}
                  </div>
                </div>

                <button data-testid="hub-meus-orcamentos" onClick={() => setView("orcamentos")}
                  className="w-full p-4 rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center gap-3 active:scale-[0.98] transition-transform text-left">
                  <ClipboardList className="w-5 h-5 text-navy shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-navy text-xs">MEUS ORÇAMENTOS</p>
                    <p className="text-[10px] text-slate-500">Consulte, altere e acompanhe seus orçamentos</p>
                  </div>
                </button>
                <Link to="/meus-servicos" data-testid="hub-consultar-agendamento"
                  className="w-full p-4 rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center gap-3 active:scale-[0.98] transition-transform">
                  <CalendarDays className="w-5 h-5 text-navy shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-navy text-xs">CONSULTAR AGENDAMENTO</p>
                    <p className="text-[10px] text-slate-500">Pendentes, confirmados, em andamento, concluídos e cancelados</p>
                  </div>
                </Link>
                {(areas?.motorista || areas?.indicador) && (
                  <button data-testid="hub-minhas-areas" onClick={() => setView("areas")}
                    className="w-full p-4 rounded-2xl bg-navy flex items-center gap-3 active:scale-[0.98] transition-transform text-left">
                    <Repeat className="w-5 h-5 text-[#F5B301] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-bold text-[#F5B301] text-xs">MINHAS ÁREAS</p>
                      <p className="text-[10px] text-slate-300">Alternar entre Cliente, Motorista e Clube de Indicação</p>
                    </div>
                  </button>
                )}
                <button data-testid="hub-minha-conta" onClick={() => setView("conta")}
                  className="w-full p-4 rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center gap-3 active:scale-[0.98] transition-transform text-left">
                  <User className="w-5 h-5 text-navy shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-navy text-xs">DADOS DA CONTA</p>
                    <p className="text-[10px] text-slate-500">Seus dados pessoais e sair</p>
                  </div>
                </button>
              </div>
            )}

            {view === "areas" && (
              <div className="space-y-3" data-testid="hub-areas">
                <p className="text-xs text-slate-500">Sua conta tem acesso a estas áreas — toque para alternar:</p>
                <div className="w-full p-4 rounded-2xl bg-white border-2 border-[#F5B301] flex items-center gap-3" data-testid="hub-area-cliente">
                  <User className="w-5 h-5 text-navy shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-navy text-xs">ÁREA DO CLIENTE</p>
                    <p className="text-[10px] text-slate-500">Você está aqui</p>
                  </div>
                </div>
                {areas?.motorista && (
                  <Link to="/motorista" data-testid="hub-area-motorista"
                    className="w-full p-4 rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center gap-3 active:scale-[0.98] transition-transform">
                    <Truck className="w-5 h-5 text-navy shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-bold text-navy text-xs">ÁREA DO MOTORISTA</p>
                      <p className="text-[10px] text-slate-500">{areas.motorista.nome} · {areas.motorista.status === "ativo" ? "ativo" : areas.motorista.status}</p>
                    </div>
                  </Link>
                )}
                {areas?.indicador && (
                  <Link to="/clube/painel" data-testid="hub-area-indicador"
                    className="w-full p-4 rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center gap-3 active:scale-[0.98] transition-transform">
                    <Star className="w-5 h-5 text-navy shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-bold text-navy text-xs">CLUBE DE INDICAÇÃO</p>
                      <p className="text-[10px] text-slate-500">{areas.indicador.id_publico} · {areas.indicador.status === "ativo" ? "ativo" : areas.indicador.status}</p>
                    </div>
                  </Link>
                )}
              </div>
            )}

            {view === "conta" && (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2" data-testid="hub-conta">
                <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-1">Dados da conta</h2>
                {(dados.nome || nome) && <p className="text-xs text-slate-600" data-testid="conta-nome"><strong>Nome:</strong> {dados.nome || nome}</p>}
                {dados.telefone && <p className="text-xs text-slate-600" data-testid="conta-telefone"><strong>Telefone:</strong> {dados.telefone}</p>}
                {dados.cpf && <p className="text-xs text-slate-600" data-testid="conta-cpf"><strong>CPF:</strong> {dados.cpf}</p>}
                {(dados.email || googleUser?.email) && <p className="text-xs text-slate-600" data-testid="conta-email"><strong>E-mail:</strong> {dados.email || googleUser?.email}</p>}
                <p className="text-[10px] text-slate-400">O acesso é identificado pelo telefone e CPF informados no orçamento (ou pela sua conta Google).</p>
                <button data-testid="hub-conta-sair" onClick={sair}
                  className="w-full mt-2 py-3 rounded-xl border-2 border-red-100 text-red-500 text-xs font-extrabold flex items-center justify-center gap-2 active:scale-95 transition-transform">
                  <LogOut className="w-4 h-4" /> SAIR DA CONTA
                </button>
              </div>
            )}

            {view === "orcamentos" && (
            <section>
              <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2">Meus orçamentos</h2>
              {rascunho && (
                <div className="bg-gold-light border border-[#F5B301]/50 rounded-2xl p-4 mb-3" data-testid="rascunho-card">
                  <p className="text-xs font-bold text-navy">Orçamento em preenchimento</p>
                  <p className="text-[10px] text-slate-500">Salvo automaticamente em {new Date(rascunho.atualizado_em).toLocaleString("pt-BR")}</p>
                  <Link to="/orcamento" data-testid="continuar-orcamento-btn"
                    className="mt-2 block w-full py-2.5 rounded-xl bg-navy text-[#F5B301] text-[11px] font-extrabold text-center active:scale-95 transition-transform">
                    CONTINUAR ORÇAMENTO
                  </Link>
                </div>
              )}
              <div className="space-y-3" data-testid="orcamentos-list">
                {orcamentos.length === 0 && <p className="text-xs text-slate-400">Nenhum orçamento aguardando pagamento.</p>}
                {orcamentos.map((o) => <OrcamentoCard key={o.quote_id} o={o} />)}
              </div>
            </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
